import React from 'react';
import { Card, Typography, Spin } from 'antd';
import { theme } from '../../theme';

const { Text } = Typography;

export interface StatCardProps {
  /** Título o etiqueta de la estadística */
  title: string;
  /** Valor a mostrar (puede ser número o string) */
  value: number | string;
  /** Color del borde superior y del valor */
  color?: string;
  /** Icono a mostrar (opcional) */
  icon?: React.ReactNode;
  /** Formato del número (si es número) */
  formatNumber?: boolean;
  /** Decimales para números (default: 0) */
  decimals?: number;
  /** Sufijo a mostrar después del valor (ej: "kg", "%", etc.) */
  suffix?: string;
  /** Texto adicional debajo del valor */
  subtitle?: string;
  /** Estado de carga (muestra spinner) */
  loading?: boolean;
  /** Estilos adicionales para la card */
  style?: React.CSSProperties;
  /** Clase CSS adicional */
  className?: string;
}

/**
 * Componente reutilizable para tarjetas de estadísticas
 * Se adapta automáticamente al contenido sin encimarse
 * 
 * @example
 * <StatCard
 *   title="Total"
 *   value={1234567}
 *   color={theme.colors.primary}
 *   formatNumber
 * />
 */
export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  color = theme.colors.primary,
  icon,
  formatNumber = false,
  decimals = 0,
  suffix = '',
  subtitle,
  loading = false,
  style = {},
  className = ''
}) => {
  // Formatear el valor si es número y se solicita formato
  const formattedValue = typeof value === 'number' && formatNumber
    ? value.toLocaleString('es-AR', { 
        minimumFractionDigits: decimals, 
        maximumFractionDigits: decimals 
      })
    : String(value);

  return (
    <Card
      className={className}
      style={{
        backgroundColor: '#fff',
        borderRadius: theme.styles.borderRadius,
        boxShadow: theme.styles.boxShadowLight,
        borderTop: `4px solid ${color}`,
        minWidth: '150px',
        flex: '1 1 auto',
        height: '100%',
        ...style
      }}
      bodyStyle={{
        padding: '16px',
        minHeight: '80px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}
    >
      {loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '80px' 
        }}>
          <Spin />
        </div>
      ) : (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          gap: '12px',
          width: '100%'
        }}>
          <div style={{ 
            flex: 1,
            minWidth: 0, // Permite que el contenido se ajuste
            overflow: 'hidden'
          }}>
            <Text 
              type="secondary" 
              style={{ 
                fontSize: '12px', 
                display: 'block',
                marginBottom: '8px',
                wordBreak: 'break-word',
                lineHeight: '1.4'
              }}
            >
              {title}
            </Text>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: 600, 
              color,
              marginTop: '4px',
              wordBreak: 'break-word',
              lineHeight: '1.2',
              overflowWrap: 'break-word'
            }}>
              {formattedValue}{suffix && ` ${suffix}`}
            </div>
            {subtitle && (
              <Text 
                style={{ 
                  fontSize: '12px', 
                  color: theme.colors.textSecondary,
                  display: 'block',
                  marginTop: '4px',
                  wordBreak: 'break-word'
                }}
              >
                {subtitle}
              </Text>
            )}
          </div>
          {icon && (
            <div style={{ 
              flexShrink: 0,
              display: 'flex',
              alignItems: 'flex-start'
            }}>
              {icon}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

/**
 * Contenedor para múltiples tarjetas de estadísticas
 * Asegura que las tarjetas se adapten al contenido sin encimarse
 */
export const StatCardsContainer: React.FC<{
  children: React.ReactNode;
  /** Espaciado entre tarjetas (default: 16px) */
  gap?: number;
  /** Columnas mínimas por tarjeta (default: 200px) */
  minColumnWidth?: string;
  className?: string;
  style?: React.CSSProperties;
}> = ({ 
  children, 
  gap = 16, 
  minColumnWidth = '200px',
  className = '',
  style = {}
}) => {
  return (
    <div
      className={className}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${minColumnWidth}, 1fr))`,
        gap: `${gap}px`,
        width: '100%',
        ...style
      }}
    >
      {children}
    </div>
  );
};

